import React, {ReactElement} from 'react'
import {useTranslation} from 'react-i18next'

import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import BasicTile from 'src/designSystem/components/molecules/BasicTile/BasicTile'
import Box from 'src/designSystem/components/atoms/Box/Box'
import Button from 'src/designSystem/components/atoms/Button/Button'
import {NamedColors} from 'src/designSystem/colors'
import {DividerWithTextOrIcon} from 'src/designSystem/components/atoms/DividerWithTextOrIcon/DividerWithTextOrIcon'
import {Color} from 'src/designSystem/types'

export type BalanceTileProps = {
  balance: number
  totalPaid?: number
  overdueAmount?: number
  onMakePayment: () => void
  isAdvance: boolean
  testID: string
}

/**
 * Displays a monetary amount with a colored background
 *
 * @param props - Component props
 * @param props.amount - The monetary amount to display
 * @param props.backgroundColor - The background color for the display
 * @param props.textColor - The text color (defaults to white)
 * @returns React element with formatted amount display
 */
const AmountDisplay = ({
  amount,
  backgroundColor,
  textColor = NamedColors.WHITE,
}: {
  amount: number
  backgroundColor: Color
  textColor?: Color
}): ReactElement => (
  <Box
    background={backgroundColor}
    paddingHorizontal="little"
    paddingVertical="tiny"
    radius="small"
  >
    <PFText variant="p_semibold" color={textColor}>
      ${amount.toFixed(2)}
    </PFText>
  </Box>
)

/**
 * Displays a row showing total paid amount with label
 *
 * @param props - Component props
 * @param props.isStandalone - Whether the component is displayed standalone (affects styling)
 * @param props.totalPaid - The total amount that has been paid
 * @param props.label - The label text to display next to the amount
 * @returns React element with formatted total paid row
 */
const TotalPaidRow = ({
  isStandalone = true,
  totalPaid,
  label,
}: {
  isStandalone?: boolean
  totalPaid: number
  label: string
}): ReactElement => (
  <Box
    background={NamedColors.WHITESMOKE}
    padding={isStandalone ? 'small' : undefined}
    radius={isStandalone ? 'medium' : 'none'}
    direction="row"
    justify="between"
    align="center"
  >
    <PFText variant="p_semibold">{label}</PFText>
    <AmountDisplay
      amount={totalPaid}
      backgroundColor={isStandalone ? NamedColors.WHITE : NamedColors.PRODUCT_BLUE}
      textColor={isStandalone ? NamedColors.BLACK : NamedColors.WHITE}
    />
  </Box>
)

/**
 * A tile to show the current balance for an advance or loan with different states based on payment status
 *
 * @param props - Component props
 * @param props.balance - The current balance amount of the loan or advance
 * @param props.totalPaid - The total amount that has been paid so far (only displayed if greater than 0)
 * @param props.overdueAmount - The amount that is currently overdue
 * @param props.onMakePayment - Callback function triggered when the Make Payment button is pressed
 * @param props.isAdvance - Whether this tile represents an advance or a loan
 * @param props.testID - Test ID for component identification in automated tests
 */
export const BalanceTile: React.FC<BalanceTileProps> = ({
  balance,
  totalPaid = 0,
  overdueAmount = 0,
  onMakePayment: handleOnMakePayment,
  isAdvance,
  testID,
}) => {
  const {t} = useTranslation('BalanceTile')

  const productType = isAdvance ? 'Advance' : 'Loan'
  const hasOverduePayment = overdueAmount > 0

  // Section for up-to-date payments
  const renderUpToDateSection = (): ReactElement => (
    <Box background={NamedColors.WHITESMOKE} padding="medium" radius="medium">
      <Box paddingBottom="small">
        <TotalPaidRow isStandalone={false} totalPaid={totalPaid} label={t('TotalPaid')} />
      </Box>
      <DividerWithTextOrIcon hasNoTextNoIcon />
      <Box paddingTop="small">
        <PFText variant="p">
          {t(isAdvance ? 'AccountUpToDateAdvance' : 'AccountUpToDateLoan')}
        </PFText>
      </Box>
    </Box>
  )

  // Section for overdue payments
  const renderOverdueSection = (): ReactElement => (
    <Box background={NamedColors.WHITESMOKE} padding="medium" radius="medium">
      <Box direction="row" justify="between" paddingBottom="small" align="center">
        <PFText variant="p_semibold">{t('OverdueBalance')}</PFText>
        <AmountDisplay
          amount={overdueAmount}
          backgroundColor={NamedColors.GOLD}
          textColor={NamedColors.BLACK}
        />
      </Box>
      <DividerWithTextOrIcon hasNoTextNoIcon />
      <Box paddingTop="small">
        <PFText variant="p">
          {t(isAdvance ? 'MakePaymentForAdvanceAccess' : 'MakePaymentForLoanAccess')}
        </PFText>
      </Box>
    </Box>
  )

  // Render payment status section based on account state
  const renderPaymentStatusSection = (): ReactElement | null => {
    if (hasOverduePayment) {
      return (
        <>
          {renderOverdueSection()}
          {totalPaid > 0 ? (
            <Box marginTop="small">
              <TotalPaidRow isStandalone totalPaid={totalPaid} label={t('TotalPaid')} />
            </Box>
          ) : null}
        </>
      )
    }

    return totalPaid > 0 ? renderUpToDateSection() : null
  }

  const paymentStatusSection = renderPaymentStatusSection()

  return (
    <BasicTile padding="medium" testID={testID}>
      {/* Balance Header */}
      <Box marginBottom="medium">
        <PFText variant="label_md">{t(`ProductTypeBalance`, {productType})}</PFText>
        <PFText testID="Amount-Id" variant="h1">
          ${balance.toFixed(2)}
        </PFText>
      </Box>

      {/* Payment Status Section */}
      {paymentStatusSection !== null ? (
        <Box marginBottom="medium">{paymentStatusSection}</Box>
      ) : null}

      {/* Make Payment Button */}
      <Button
        mode="primary"
        size="medium"
        width="100%"
        onPress={handleOnMakePayment}
        testID={`${testID}-MakePaymentBtn`}
      >
        {t('MakePayment')}
      </Button>
    </BasicTile>
  )
}
